if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      constructor() {
        super();

        this.handleAnchorClick = this.handleAnchorClick.bind(this);
        this.handleOptionClick = this.handleOptionClick.bind(this);
        this.handleCtaClick = this.handleCtaClick.bind(this);
        this.handleProductInfoLoaded = this.handleProductInfoLoaded.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.sectionId = this.dataset.sectionId;
        this.mode = this.dataset.mode || 'add-to-cart';
        this.customizeLabel = this.dataset.customizeLabel || '';
        this.anchorButtons = Array.from(this.querySelectorAll('[data-sticky-target-anchor]'));
        this.optionButtons = Array.from(this.querySelectorAll('[data-option-value-id]'));
        this.ctaButton = this.querySelector('[data-sticky-cta]');
        this.ctaText = this.ctaButton?.querySelector('span');

        this.anchorButtons.forEach((button) => button.addEventListener('click', this.handleAnchorClick));
        this.optionButtons.forEach((button) => button.addEventListener('click', this.handleOptionClick));
        this.ctaButton?.addEventListener('click', this.handleCtaClick);
        this.productInfo.addEventListener('product-info:loaded', this.handleProductInfoLoaded);

        this.setupVisibilityObserver();
        this.setupAnchorObserver();
        this.setupSubmitObserver();
        this.syncState();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        }
      }

      disconnectedCallback() {
        this.anchorButtons?.forEach((button) => button.removeEventListener('click', this.handleAnchorClick));
        this.optionButtons?.forEach((button) => button.removeEventListener('click', this.handleOptionClick));
        this.ctaButton?.removeEventListener('click', this.handleCtaClick);
        this.productInfo?.removeEventListener('product-info:loaded', this.handleProductInfoLoaded);
        this.visibilityObserver?.disconnect();
        this.sectionAnchorObserver?.disconnect();
        this.submitStateObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      handleProductInfoLoaded() {
        this.setupVisibilityObserver();
        this.setupAnchorObserver();
        this.setupSubmitObserver();
        this.syncState();
      }

      handleVariantChange(event) {
        if (!event?.data || event.data.sectionId !== this.sectionId) return;

        window.requestAnimationFrame(() => {
          this.setupSubmitObserver();
          this.syncState();
        });
      }

      setupVisibilityObserver() {
        this.visibilityObserver?.disconnect();

        const trigger = this.getVisibilityTrigger();
        if (!trigger) {
          this.hidden = true;
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.hidden = !shouldShow;
          },
          {
            threshold: 0.05,
          }
        );

        this.visibilityObserver.observe(trigger);
      }

      setupAnchorObserver() {
        this.sectionAnchorObserver?.disconnect();
        this.anchorTargets = new Map();

        this.anchorButtons.forEach((button) => {
          const anchorName = button.dataset.stickyTargetAnchor;
          const target = this.findTarget(anchorName);

          button.hidden = !target;
          if (!target) return;

          this.anchorTargets.set(anchorName, target);
        });

        const anchorsNav = this.querySelector('.product-sticky-bar__anchors');
        if (anchorsNav) {
          anchorsNav.hidden = this.anchorTargets.size === 0;
        }

        if (!this.anchorTargets.size) return;

        this.sectionAnchorObserver = new IntersectionObserver(
          (entries) => {
            const visibleEntries = entries
              .filter((entry) => entry.isIntersecting)
              .sort((left, right) => Math.abs(left.boundingClientRect.top) - Math.abs(right.boundingClientRect.top));

            if (!visibleEntries.length) return;

            const activeEntry = visibleEntries[0];
            for (const [anchorName, target] of this.anchorTargets.entries()) {
              if (target === activeEntry.target) {
                this.setActiveAnchor(anchorName);
                break;
              }
            }
          },
          {
            rootMargin: '-35% 0px -45% 0px',
            threshold: [0.1, 0.35, 0.6],
          }
        );

        this.anchorTargets.forEach((target) => this.sectionAnchorObserver.observe(target));
      }

      setupSubmitObserver() {
        this.submitStateObserver?.disconnect();

        const submitButton = this.getOriginalSubmitButton();
        if (!submitButton) return;

        this.submitStateObserver = new MutationObserver(() => this.syncSubmitState());
        this.submitStateObserver.observe(submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

      syncState() {
        this.syncSubmitState();
        this.syncOptionButtons();
      }

      syncSubmitState() {
        if (!this.ctaButton) return;

        const submitButton = this.getOriginalSubmitButton();
        if (!submitButton) return;

        const sourceText = submitButton.querySelector('span')?.textContent?.trim() || '';
        const isDisabled =
          submitButton.hasAttribute('disabled') || submitButton.getAttribute('aria-disabled') === 'true';
        const isLoading = submitButton.classList.contains('loading');

        this.ctaButton.disabled = isDisabled;
        this.ctaButton.classList.toggle('loading', isLoading);

        if (!this.ctaText) return;

        if (this.mode === 'customize') {
          this.ctaText.textContent = isDisabled && sourceText ? sourceText : this.customizeLabel || sourceText;
          return;
        }

        if (sourceText) {
          this.ctaText.textContent = sourceText;
        }
      }

      syncOptionButtons() {
        if (!this.optionButtons.length) return;

        let hasVisibleOptions = false;

        this.optionButtons.forEach((button) => {
          const sourceOption = this.findSourceOption(button.dataset.optionValueId);
          if (!sourceOption) {
            button.hidden = true;
            return;
          }

          const isSelected = sourceOption.selected || sourceOption.checked;
          const isDisabled = sourceOption.disabled || sourceOption.classList.contains('disabled');

          button.hidden = false;
          button.disabled = isDisabled;
          button.classList.toggle('is-selected', Boolean(isSelected));
          hasVisibleOptions = true;
        });

        const optionsContainer = this.querySelector('.product-sticky-bar__options');
        if (optionsContainer) {
          optionsContainer.hidden = !hasVisibleOptions;
        }
      }

      handleAnchorClick(event) {
        const button = event.currentTarget;
        const anchorName = button.dataset.stickyTargetAnchor;
        const target = this.findTarget(anchorName);
        if (!target) return;

        this.setActiveAnchor(anchorName);
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      handleOptionClick(event) {
        const button = event.currentTarget;
        const sourceOption = this.findSourceOption(button.dataset.optionValueId);
        if (!sourceOption || button.disabled) return;

        if (sourceOption.tagName === 'OPTION') {
          const select = sourceOption.parentElement;
          if (!select) return;
          select.value = sourceOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        sourceOption.checked = true;
        sourceOption.dispatchEvent(new Event('change', { bubbles: true }));
      }

      handleCtaClick() {
        if (this.mode === 'customize') {
          const target =
            this.findTarget('passport-customizer') ||
            this.productInfo.querySelector(`#ProductInfo-${this.sectionId}`) ||
            this.productInfo;

          target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        const submitButton = this.getOriginalSubmitButton();
        const isDisabled =
          !submitButton ||
          submitButton.disabled ||
          submitButton.getAttribute('aria-disabled') === 'true';

        if (isDisabled) return;

        this.ctaButton?.classList.add('loading');
        this.ctaButton.disabled = true;
        submitButton.click();
      }

      setActiveAnchor(activeAnchorName) {
        this.anchorButtons.forEach((button) => {
          const isActive = button.dataset.stickyTargetAnchor === activeAnchorName;
          button.classList.toggle('is-active', isActive);
        });
      }

      findTarget(anchorName) {
        if (!anchorName) return null;

        if (anchorName === 'product-info') {
          return this.productInfo.querySelector(`#ProductInfo-${this.sectionId}`) || this.productInfo;
        }

        return document.querySelector(`[data-product-anchor="${anchorName}"]`);
      }

      findSourceOption(optionValueId) {
        if (!optionValueId) return null;
        return this.productInfo.querySelector(`variant-selects [data-option-value-id="${optionValueId}"]`);
      }
      getOriginalSubmitButton() {
        return this.productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      }

      getVisibilityTrigger() {
        return this.productInfo.querySelector('product-form') || this.getOriginalSubmitButton();
      }
    }
  );
}
